/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/14/22
 */

import React from "react";
import {Button, Popover, Stack, SxProps, Theme, Tooltip, useTheme} from "@mui/material";
import {EMPLOYEE_COL_WIDTH} from "./sx";
import {addMinutes, differenceInMinutes, startOfDay} from "date-fns";

export interface GenericViewportEvent {
	title: string
	typeId: string
	startDatetime: Date
	endDatetime: Date
}

export interface CalendarEventProps {
	viewportRange: Interval
	rangeResolution: number
	event: GenericViewportEvent
	normalizeTimezone: string | false
	tooltip: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal
	details: React.ReactNode | boolean
	snapTo?: "start" | "end"
	viewportIcon?: any
	getViewportLabel?: (width: number) => string | null | undefined
	onClick?: () => void
	sx: SxProps
	viewportSx?: SxProps
}

export const eventSx = (theme: Theme, typeId?: string, tentative?: boolean) => {
	if (!typeId) return {};
	const colors: Record<string, string> = {
		pto: theme.palette.mode === "light" ? "#B3D4FF" : "#36648B",
		uto: theme.palette.mode === "light" ? "#6E7B8B" : "#42526e",
		act: theme.palette.mode === "light" ? "#FFF0B3" : "#E3A869",
		im: theme.palette.mode === "light" ? "#57D9A3" : "#00875A",
		em: theme.palette.mode === "light" ? "#FF8F73" : "#BC8F8F",
		evt: theme.palette.mode === "light" ? "#C0B6F2" : "#6554C0",
		a: theme.palette.mode === "light" ? "#E6E6FA" : "#8B8386"
	};
	const zIndex: Record<string, number> = {
		uto: 70, a: 80, pto: 60, evt: 55
	};
	return typeId in colors ? {
		backgroundColor: tentative ? undefined: colors[typeId],
		color: theme.palette.getContrastText(colors[typeId]),
		backgroundImage: tentative ? `repeating-linear-gradient(-45deg, ${theme.palette.background.paper} 0 5px, ${colors[typeId]} 5px 10px)` : undefined,
		zIndex: zIndex[typeId] ?? 50,
	} : {};
}

export default function ViewportEvent(props: CalendarEventProps) {

	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const [left, width] = React.useMemo(() => {
		const offset = EMPLOYEE_COL_WIDTH;
		let positionStart = props.event.startDatetime, positionEnd = props.event.endDatetime;
		const normalizePosition = props.rangeResolution < 1 ? props.normalizeTimezone : false;
		if (props.normalizeTimezone && normalizePosition) {
			const localStart = new Date(props.event.startDatetime.toLocaleString("en-US", {timeZone: props.normalizeTimezone}));
			const diffInMinutes = differenceInMinutes(localStart, props.event.startDatetime);
			positionStart = addMinutes(positionStart, diffInMinutes);
			positionEnd = addMinutes(positionEnd, diffInMinutes);
		}
		let left = offset + Math.max(
			0,
			Math.round(differenceInMinutes(positionStart, props.viewportRange.start) * props.rangeResolution)
		);
		let width = Math.min(
			Math.round(differenceInMinutes(positionEnd, Math.max(positionStart.getTime(), new Date(props.viewportRange.start).getTime())) * props.rangeResolution),
			differenceInMinutes(props.viewportRange.end, props.viewportRange.start) * props.rangeResolution + offset - left
		);

		if (width < 21) width = 21;

		if (props.snapTo) {
			left -= Math.round(differenceInMinutes(positionStart, startOfDay(positionStart)) * props.rangeResolution);
			if (props.snapTo === "end") left += 20;
		}
		return [left, width-1];
	}, [props.event.endDatetime, props.event.startDatetime, props.normalizeTimezone, props.rangeResolution, props.snapTo, props.viewportRange.end, props.viewportRange.start]);

	const theme = useTheme();
	const label = props.getViewportLabel?.(width);

	return (
		<Stack sx={props.viewportSx}>
			<Tooltip title={props.tooltip}>
				<Button
					sx={{
						left,
						width,
						height: "1.7em",
						position: "absolute",
						backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? "300" : "600"],
						whiteSpace: "nowrap",
						textTransform: "inherit",
						padding: "0 4px",
						margin: "1px",
						zIndex: 100,
						minWidth: "20px",
						textOverflow: "ellipsis",
						overflow: "hidden",
						...props.sx
					}}
					color={"inherit"}
					variant={"contained"}
					onClick={props.onClick ?? handleClick}
				>
					<Stack direction={"row"} alignItems={"center"} spacing={.5}>
						{props.viewportIcon}
						{label && <Stack>{label}</Stack>}
					</Stack>
				</Button>
			</Tooltip>
			{props.details &&
			<Popover
				open={anchorEl !== null}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'center',
				}}
			>
				{props.details}
			</Popover>}
		</Stack>
	);

}